import { Alert, Button, DatePicker } from '@heroui/react';
import { Icon } from '@iconify/react';
import { parseDate } from '@internationalized/date';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import { useUpdatePatientMutation } from '@/patient-app-common/api/patientApi';
import usePatientDates from '@/patient-app-common/hooks/usePatientDates';

export function DemoDates({ patient }) {
  const [updatePatient, { isLoading, isSuccess, isError }] =
    useUpdatePatientMutation();
  const { patientDates } = usePatientDates();
  const { signupDate, surgeryDate, dischargeDate, activationDate } =
    patientDates;

  const [dates, setDates] = useState({});
  const [errorFields, setErrorFields] = useState([]);
  const [selectedParam, setSelectedParam] = useState('');

  const INPUTS = [
    {
      param: 'createdAt',
      label: 'Sign up date',
      date: signupDate,
    },
    {
      param: 'activationDate',
      label: 'Activation Date',
      date: activationDate,
    },
    {
      param: 'surgeryDate',
      label: patient.maternityProgram ? 'Due date' : 'Procedure date',
      hidden: patient.nonSurgicalProgram,
      date: surgeryDate,
    },
    {
      param: 'dischargeDate',
      label: 'Discharge date',
      hidden: patient.nonSurgicalProgram,
      date: dischargeDate,
    },
  ];

  useEffect(() => {
    INPUTS.forEach((input) => {
      const { date, param } = input;
      if (date) {
        setDates({ ...dates, [param]: date });
      }
    });
  }, []);

  const validateDate = (param) => {
    if (errorFields.includes(param)) {
      return false;
    }

    const date = dates[param];
    const valid = dayjs(date, 'x', true).isValid();

    if (!date || !valid) {
      setErrorFields(_.sortedUniq([...errorFields, param]));
      return false;
    }

    return true;
  };

  const setProgramDate = (param) => {
    const valid = validateDate(param);

    if (!valid) return;

    setSelectedParam(param);

    try {
      updatePatient({
        completedDate: 0,
        [param]: dates[param],
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDateChange = (date, param) => {
    if (!date) {
      setErrorFields(_.sortedUniq([...errorFields, param]));
      return;
    }

    setErrorFields(_.without(errorFields, param));

    const formattedDate = dayjs(date.toString()).unix();
    setDates({ ...dates, [param]: formattedDate });
  };

  return (
    <div className="space-y-4">
      <Alert
        className="max-w-lg"
        color="danger"
        description="An error occured and your changes were not saved."
        hideIconWrapper
        isVisible={isError}
        variant="faded"
      />
      <Alert
        className="max-w-lg"
        color="success"
        description="Your changes were saved."
        hideIconWrapper
        isVisible={isSuccess}
        variant="faded"
      />
      {INPUTS.filter((input) => !input.hidden).map((input) => (
        <div className="w-full inline-flex gap-4 items-center">
          <DatePicker
            className="max-w-lg"
            color="primary"
            errorMessage="Enter a valid date"
            isInvalid={errorFields.includes(input.param)}
            label={input.label}
            onChange={(date) => handleDateChange(date, input.param)}
            selectorButtonPlacement="start"
            variant="underlined"
            defaultValue={
              input.date
                ? parseDate(dayjs(input.date).format('YYYY-MM-DD').toString())
                : null
            }
          />
          <Button
            color="primary"
            isLoading={isLoading && selectedParam === input.param}
            onPress={() => setProgramDate(input.param)}
            startContent={
              !isLoading && <Icon icon="tabler:edit" className="text-lg" />
            }
            variant="bordered"
          >
            Save
          </Button>
        </div>
      ))}
    </div>
  );
}
